"use client";

// Error boundary for the app subdomain. Next.js renders this whenever a
// client-side render or RSC error propagates here. Shows the actual
// message + stack on screen so the operator can copy-paste it to us
// instead of needing to open Chrome DevTools on their phone.

import { useEffect } from "react";
import Link from "next/link";
import { AlertTriangle, RotateCcw, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log everything we know — name, message, cause, custom props — so
    // remote debugging via the on-screen renderer below has more to chew on.
    console.error("[app-error]", {
      name: error.name,
      message: error.message,
      digest: error.digest,
      stack: error.stack,
      cause: (error as { cause?: unknown }).cause,
      // shallow extras
      ...Object.fromEntries(
        Object.getOwnPropertyNames(error).map((k) => [
          k,
          (error as unknown as Record<string, unknown>)[k],
        ])
      ),
    });
  }, [error]);

  // Render any extra string fields the error carries (Next.js sometimes
  // attaches { digest } and Drizzle attaches { code }).
  const extras: Array<[string, string]> = [];
  for (const k of Object.getOwnPropertyNames(error)) {
    if (k === "message" || k === "stack" || k === "name") continue;
    const v = (error as unknown as Record<string, unknown>)[k];
    if (typeof v === "string" && v) extras.push([k, v]);
    else if (typeof v === "number") extras.push([k, String(v)]);
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-6">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-6 w-6 text-amber-500" />
        <h1 className="text-2xl font-bold">Something went wrong</h1>
      </div>
      <p className="text-muted-foreground text-sm">
        The page crashed before it could render. Most of the time refreshing
        or signing in again fixes it. If you can, copy the message below and
        send it along.
      </p>

      <div className="rounded-lg border border-amber-600/30 bg-amber-950/20 p-4">
        <p className="font-mono text-xs text-amber-200 whitespace-pre-wrap break-words">
          <strong>{error.name || "Error"}</strong>: {error.message || "(no message)"}
        </p>
        {extras.length > 0 && (
          <ul className="mt-2 space-y-0.5 font-mono text-[10px] text-amber-200/70">
            {extras.map(([k, v]) => (
              <li key={k}><strong>{k}:</strong> {v}</li>
            ))}
          </ul>
        )}
        {error.stack && (
          <details className="mt-3" open>
            <summary className="cursor-pointer text-xs text-amber-300/80">
              Stack trace
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all text-[10px] text-amber-200/70">
              {error.stack}
            </pre>
          </details>
        )}
      </div>

      <div className="flex gap-2">
        <Button onClick={() => reset()}>
          <RotateCcw className="h-4 w-4" />
          Try again
        </Button>
        <Link href="/dashboard">
          <Button variant="outline">
            <ArrowLeft className="h-4 w-4" />
            Back to dashboard
          </Button>
        </Link>
      </div>
    </div>
  );
}
